import { MCIssueEntity } from '../types/MCEntity'
import { EnrichedIssueEntity, IssueEntity } from '../types/IssueEntity'
import ApiBase from './Api'
import { getIssueTotalUnitSize, getInvestorAmountInCash, getInvestorAmountInPercentage } from './utils'
import { getStoredLanguage, resolveLanguage } from '../i18n/languagePreference'

const setupDerivedFields = (issue: IssueEntity) => {
    const calcTotalCommitment = (approxCommitment, approxGuarantor, approxTotalCommitment) => {
        const ac = approxCommitment
        const ag = approxGuarantor
        const atc = approxTotalCommitment
        let tc: number | null = null

        if (ac != null || ag != null) {
            if (ac && ag) {
                tc = parseFloat(ac) + parseFloat(ag)
            } else if (ac) {
                tc = parseFloat(ac)
            } else {
                tc = parseFloat(ag)
            }
        } else {
            tc = atc != null ? parseFloat(atc) : null
        }
        return tc
    }
    // issue.shareSubPrice = issue.unitSubPrice / issue.sharesInUnit
    const totalUnitSize = getIssueTotalUnitSize(issue)

    return {
        ...issue,
        investors: issue.investors.map((i) => ({
            ...i,
            amountInCash: getInvestorAmountInCash(i, totalUnitSize),
            amountInPercentage: getInvestorAmountInPercentage(i, totalUnitSize),
        })),
        totalCommitment: calcTotalCommitment(
            issue.approxCommitment,
            issue.approxGuarantor,
            issue.approxTotalCommitment,
        ),
        totalOfferedUnits:
            issue.offeredUnits === null && issue.offeredUnitsUnlisted === null
                ? null
                : (issue.offeredUnits ?? 0) + (issue.offeredUnitsUnlisted ?? 0),
        totalUnitSize,
    } as EnrichedIssueEntity
}

const ISSUE_API_PATHS = {
    ISSUES: '/issues',
    ISSUE: (id: string) => `/issues/${id}`,
    ADMIN_ISSUES: '/admin/issues',
    ADMIN_ISSUE: (id: string) => `/admin/issues/${id}`,

    ADMIN_MCISSUES: '/admin/mcissues',
    ADMIN_MCISSUE: (id: string) => `/admin/mcissues/${id}`,
}

class IssueApi extends ApiBase {
    async findAll(): Promise<EnrichedIssueEntity[]> {
        const fetchedIssues: IssueEntity[] = await this._getRequest(ISSUE_API_PATHS.ISSUES)

        const enrichedIssues = fetchedIssues.map((issue) => setupDerivedFields(issue))
        return enrichedIssues
    }

    async findByIssueId(issueId: string, enrichIssue?: false): Promise<IssueEntity | null>
    async findByIssueId(issueId: string, enrichIssue?: true): Promise<EnrichedIssueEntity | null>
    async findByIssueId(issueId: string, enrichIssue = true): Promise<IssueEntity | null> {
        if (!issueId) {
            return null
        }
        const preferredLanguage = resolveLanguage(getStoredLanguage())
        const fetchedIssue: IssueEntity = await this._getRequest(ISSUE_API_PATHS.ISSUE(issueId), {
            queryParameters: { preferredLanguage },
        })

        if (enrichIssue) {
            return setupDerivedFields(fetchedIssue)
        }

        return fetchedIssue
    }

    async findByCompanyId(
        companyId: string,
        enrichIssue = true,
        { excludeCancelled = false }: { excludeCancelled?: boolean } = {},
    ): Promise<IssueEntity[]> {
        if (!companyId) {
            return []
        }
        const fetchedIssues = await this._getRequest(ISSUE_API_PATHS.ISSUES, {
            queryParameters: {
                companyId,
                ...(excludeCancelled ? { excludeCancelled: 'true' } : {}),
            },
        })

        if (enrichIssue) {
            const enrichedIssues = fetchedIssues.map((issue) => setupDerivedFields(issue))
            return enrichedIssues
        }

        return fetchedIssues
    }
    async findByShareId(shareId: string | null): Promise<IssueEntity[]> {
        if (!shareId) {
            return []
        }
        const fetchedIssues = await this._getRequest(ISSUE_API_PATHS.ISSUES, {
            queryParameters: { shareId },
        })
        return fetchedIssues as IssueEntity[]
    }

    async findByAdvisorId(advisorId: string): Promise<IssueEntity[]> {
        if (!advisorId) {
            return []
        }
        const fetchedIssues = await this._getRequest(ISSUE_API_PATHS.ISSUES, {
            queryParameters: { advisorId },
        })

        return fetchedIssues as IssueEntity[]
    }

    async create(
        data: Pick<IssueEntity, 'shareId' | 'type'> & { ipoData?: { instrumentName: string } },
    ): Promise<IssueEntity | null> {
        if (!data) {
            return null
        }

        const createdIssue: IssueEntity = await this._postRequest(ISSUE_API_PATHS.ADMIN_ISSUES, data)

        return createdIssue
    }

    async update(issueId: string, data): Promise<IssueEntity | null> {
        if (!data) {
            return null
        }

        const updatedIssue: IssueEntity = await this._putRequest(ISSUE_API_PATHS.ADMIN_ISSUE(issueId), data)

        return updatedIssue
    }

    /**
     * Changes specific fields of an issue. The difference between this method and the update method is that this method
     * only updates the fields that are passed in the data object. The update method, on the other hand, updates all the fields
     * of the issue.
     * Note that only certain fields are allowed to be updated with the PATCH method. Currently only the isinUnits and isinRights
     * fields are allowed.
     */
    async patchFields(issueId: string, data: Record<string, string | number>): Promise<IssueEntity | null> {
        if (!data || Object.keys(data).length === 0) {
            return null
        }
        // whitelistedFields are the fields that are allowed to be updated with the PATCH method
        const whitelistedFields = ['isinUnits', 'isinRights']
        const fieldsToUpdate: Record<string, string | number> = {}
        whitelistedFields.forEach((field) => {
            if (data[field] !== undefined) {
                fieldsToUpdate[field] = data[field]
            }
        })
        if (Object.keys(fieldsToUpdate).length === 0) {
            console.error('No whitelisted fields to update. ', data)
            return null
        }
        const updatedIssue = await this._patchRequest(ISSUE_API_PATHS.ADMIN_ISSUE(issueId), fieldsToUpdate)
        return updatedIssue
    }

    async delete(issueId: string): Promise<null> {
        const deletedIssue = await this._deleteRequest(ISSUE_API_PATHS.ADMIN_ISSUE(issueId))
        return deletedIssue
    }

    async findMcIssues(): Promise<MCIssueEntity[]> {
        const fetchedMcIssues = await this._getRequest(ISSUE_API_PATHS.ADMIN_MCISSUES)
        return fetchedMcIssues
    }

    async archiveMcIssue(instrumentHistoryId: string, flagAsConvertible = false): Promise<null> {
        await this._patchRequest(ISSUE_API_PATHS.ADMIN_MCISSUE(instrumentHistoryId), {
            archived: true,
            isConvertible: flagAsConvertible,
        })
        return null
    }

    /**
     * Records the match between an issue and an instrument history event,
     * which indicates that we have handled this instrument history as an issue.
     */
    async matchIssueToInstrumentHistory(issueId: string, instrumentHistoryId: string) {
        const matchedResponse = await this._patchRequest(ISSUE_API_PATHS.ADMIN_ISSUE(issueId), {
            matchedTo: instrumentHistoryId,
        })
        return matchedResponse
    }
}

export default IssueApi
